import React, { useState, useEffect } from 'react';
import { Table, Button, Space, message, Switch, Input, Form, Typography } from 'antd';
import { useNavigate } from 'react-router-dom';
import { getCustomers, toggleCustomerStatus, toggleCustomerAddressUpdate, updateCustomerInfo, updateCustomerPassword } from '../../services/userService';
import CustomerCourierModal from './CustomerCourierModal';

const { Link } = Typography;

const EditableCell = ({ editing, dataIndex, title, inputType, record, index, children, ...restProps }) => {
  const inputNode = inputType === 'password' ? 
    <Input.Password placeholder="输入新密码" /> : 
    <Input />;

  return (
    <td {...restProps}>
      {editing ? (
        <Form.Item
          name={dataIndex}
          style={{ margin: 0 }}
          rules={[
            {
              required: true,
              message: `请输入${title}!`,
            },
          ]}
        >
          {inputNode}
        </Form.Item>
      ) : (
        <div className="editable-cell-value-wrap" style={{ paddingRight: 24 }}>
          {children}
        </div>
      )}
    </td>
  );
};

const CustomerManagement = () => {
  const [form] = Form.useForm();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const [updatingAddressPermission, setUpdatingAddressPermission] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [editingKey, setEditingKey] = useState('');
  const navigate = useNavigate();

  const isEditing = (record) => record.id === editingKey;

  const edit = (record) => {
    form.setFieldsValue({
      username: record.username,
      company_name: record.company_name,
      phone: record.phone,
      password: '',
    });
    setEditingKey(record.id);
  };

  const cancel = () => {
    setEditingKey('');
  };

  const save = async (record) => {
    try {
      const row = await form.validateFields();
      const newData = [...customers];
      const index = newData.findIndex((item) => record.id === item.id);

      if (index > -1) {
        const item = newData[index];
        
        try {
          const { password, ...updateData } = row;
          await updateCustomerInfo(record.id, updateData);
          
          if (password) {
            await updateCustomerPassword(record.id, password);
          }
          
          newData.splice(index, 1, {
            ...item,
            ...row,
          });
          setCustomers(newData);
          setEditingKey('');
          message.success('更新成功');
        } catch (error) {
          message.error('更新失败');
        }
      }
    } catch (errInfo) {
      console.log('Validate Failed:', errInfo);
    }
  };

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const data = await getCustomers();
      setCustomers(data);
    } catch (error) {
      message.error('获取客户列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  const handleToggleStatus = async (record, checked, e) => {
    e.stopPropagation();
    setUpdatingStatus(record.id);
    try {
      setCustomers(prevCustomers =>
        prevCustomers.map(customer =>
          customer.id === record.id
            ? { ...customer, is_active: checked }
            : customer
        )
      );

      await toggleCustomerStatus(record.id, checked);
      message.success(checked ? '客户已启用' : '客户已禁用');
    } catch (error) {
      setCustomers(prevCustomers =>
        prevCustomers.map(customer =>
          customer.id === record.id
            ? { ...customer, is_active: !checked }
            : customer
        )
      );
      message.error('操作失败');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const formatDateTime = (dateTimeStr) => {
    if (!dateTimeStr) return '从未登录';
    const date = new Date(dateTimeStr);
    return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日 ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
  };

  const calculateDaysSinceRegistration = (dateJoined) => {
    if (!dateJoined) return '未知';
    const registrationDate = new Date(dateJoined);
    const today = new Date();
    const diffTime = Math.abs(today - registrationDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return `${diffDays}天`;
  };

  const handleAddCustomer = () => {
    navigate('/register');
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      sorter: (a, b) => a.id - b.id,
      defaultSortOrder: 'descend',
    },
    {
      title: '用户名(登录)',
      dataIndex: 'username',
      key: 'username',
      editable: true,
      onCell: (record) => ({
        onClick: () => !isEditing(record) && edit(record)
      }),
    },
    {
      title: '密码',
      dataIndex: 'password',
      key: 'password',
      editable: true,
      onCell: (record) => ({
        onClick: () => !isEditing(record) && edit(record)
      }),
      render: (_, record) => {
        const editable = isEditing(record);
        return editable ? (
          <Form.Item
            name="password"
            style={{ margin: 0 }}
          >
            <Input.Password placeholder="输入新密码" />
          </Form.Item>
        ) : (
          '******'
        );
      },
    },
    {
      title: '公司名称',
      dataIndex: 'company_name',
      key: 'company_name',
      editable: true,
      onCell: (record) => ({
        onClick: () => !isEditing(record) && edit(record)
      }),
    },
    {
      title: '手机号',
      dataIndex: 'phone',
      key: 'phone',
      editable: true,
      onCell: (record) => ({
        onClick: () => !isEditing(record) && edit(record)
      }),
    },
    {
      title: '订单数量',
      dataIndex: 'order_count',
      key: 'order_count',
      sorter: (a, b) => a.order_count - b.order_count,
    },
    {
      title: '注册时长',
      dataIndex: 'date_joined',
      key: 'date_joined',
      sorter: (a, b) => new Date(a.date_joined) - new Date(b.date_joined),
      render: (dateJoined) => calculateDaysSinceRegistration(dateJoined),
    },
    {
      title: '最后登录',
      dataIndex: 'last_login',
      key: 'last_login',
      render: (lastLogin) => formatDateTime(lastLogin),
    },
    {
      title: '账户状态',
      dataIndex: 'is_active',
      key: 'account_status',
      render: (isActive, record) => (
        <Switch
          checked={isActive}
          onChange={(checked, e) => handleToggleStatus(record, checked, e)}
          checkedChildren="启用"
          unCheckedChildren="禁用"
          loading={updatingStatus === record.id}
        />
      ),
    },
    {
      title: '可换地址',
      dataIndex: 'can_update_address',
      key: 'can_update_address',
      render: (canUpdate, record) => (
        <Switch
          checked={canUpdate}
          onChange={(checked, e) => handleToggleAddressUpdate(record, checked, e)}
          checkedChildren="允许"
          unCheckedChildren="禁止"
          loading={updatingAddressPermission === record.id}
        />
      ),
    },
    {
      title: '指定快递',
      key: 'courier',
      render: (_, record) => (
        <Button type="link" onClick={(e) => handleEditCouriers(record.id, e)}>指定快递</Button>
      ),
    },
    {
      title: '操作',
      dataIndex: 'operation',
      render: (_, record) => {
        const editable = isEditing(record);
        return editable ? (
          <span>
            <Link onClick={() => save(record)} style={{ marginRight: 8 }}>
              保存
            </Link>
            <Link onClick={cancel}>取消</Link>
          </span>
        ) : null;
      },
    },
  ];

  const mergedColumns = columns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record) => ({
        record,
        inputType: col.dataIndex === 'password' ? 'password' : 'text',
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
        onClick: () => !isEditing(record) && edit(record),
      }),
    };
  });

  const handleEditCouriers = (customerId, e) => {
    e.stopPropagation();
    setSelectedCustomerId(customerId);
    setModalVisible(true);
  };

  const handleToggleAddressUpdate = async (record, checked, e) => {
    e.stopPropagation();
    setUpdatingAddressPermission(record.id);
    try {
      setCustomers(prevCustomers =>
        prevCustomers.map(customer =>
          customer.id === record.id
            ? { ...customer, can_update_address: checked }
            : customer
        )
      );

      await toggleCustomerAddressUpdate(record.id, checked);
      message.success(checked ? '已允许修改地址' : '已禁止修改地址');
    } catch (error) {
      setCustomers(prevCustomers =>
        prevCustomers.map(customer =>
          customer.id === record.id
            ? { ...customer, can_update_address: !checked }
            : customer
        )
      );
      message.error('操作失败');
    } finally {
      setUpdatingAddressPermission(null);
    }
  };

  const handleModalClose = () => {
    setModalVisible(false);
    setSelectedCustomerId(null);
  };

  return (
    <div>
      <CustomerCourierModal
        visible={modalVisible}
        customerId={selectedCustomerId}
        onClose={handleModalClose}
      />
      <div style={{ marginBottom: 16 }}>
        <Button type="primary" onClick={handleAddCustomer}>新增客户</Button>
      </div>
      <Form form={form} component={false}>
        <Table
          components={{
            body: {
              cell: EditableCell,
            },
          }}
          columns={mergedColumns}
          dataSource={customers}
          rowKey="id"
          loading={loading}
          size="small"
          pagination={{
            total: customers.length,
            pageSize: 20,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 个客户`
          }}
        />
      </Form>
    </div>
  );
};

export default CustomerManagement;
